import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box
} from '@mui/material';
import api from '../services/api';
import { fetchCurrentUser } from '../services/auth';
import ExitAuthorizationPDF from './ExitAuthorizationPDF';
import { PDFViewer } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';

const ExitAuthorizationForm = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submittedData, setSubmittedData] = useState(null);
  const [isExitAuthCreated, setIsExitAuthCreated] = useState(false);
  const [userRole, setUserRole] = useState(null); // 1 for admin, other values for non-admin
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  // Confirmation step states
  const [confirmationData, setConfirmationData] = useState(null);
  const [loadingConfirmation, setLoadingConfirmation] = useState(false);

  // Fetch employees
  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const currentUser = await fetchCurrentUser();
          const roleId = Number(currentUser?.role_id ?? currentUser?.role);
          setUserRole(roleId);
          
          // Fetch employees based on user role
          if (roleId === 1) {
            // ADMIN: afficher tous les employés
            const employeesResponse = await api.get('/employees');
            // Trier les employés par nom (first_name puis last_name)
            const sortedEmployees = employeesResponse.data.sort((a, b) => {
              const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
              const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
              return nameA.localeCompare(nameB);
            });
            setEmployees(sortedEmployees);
          } else if (roleId === 5) {
            // ADMIN-TENES: afficher les employés du site TENES uniquement
            const employeesResponse = await api.get('/employees');
            // Trier les employés par nom (first_name puis last_name)
            const sortedEmployees = employeesResponse.data.sort((a, b) => {
              const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
              const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
              return nameA.localeCompare(nameB);
            });
            setEmployees(sortedEmployees);
          } else {
            // AUTRES: afficher le user connecté
            const empResponse = await api.get('/employees/me');
            setEmployee(empResponse.data);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
        }
      };
      fetchData();
    }
  }, [open]);

  const formik = useFormik({
    initialValues: {
      employeeId: employee?.id || '', // Pre-fill with current employee ID if available
      reason: '',
      sortie_date: new Date().toISOString().split('T')[0],
      sortie_time: '09:00',
      entree_date: new Date().toISOString().split('T')[0],
      entree_time: '17:00',
    },
    validationSchema: Yup.object({
      employeeId: Yup.number().required('Champ requis.'),
      reason: Yup.string().required('Champ requis.'),
      sortie_date: Yup.date()
        .min(new Date(new Date().setHours(0, 0, 0, 0)), 'La date ne peut pas être dans le passé')
        .required('Champ requis.'),
      sortie_time: Yup.string()
        .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Format HH:mm requis.')
        .required('Champ requis.'),
      entree_date: Yup.date()
        .min(Yup.ref('sortie_date'), "La date d'entrée doit être après la date de sortie.")
        .required('Champ requis.'),
      entree_time: Yup.string()
        .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Format HH:mm requis.')
        .required('Champ requis.'),
    }),
    onSubmit: (values) => {
      setConfirmationData(values);
    },
  });

  // Handle confirmation step
  const handleConfirm = async () => {
    if (loadingConfirmation) {
      return; // Prevent multiple clicks
    }
    
    try {
      setLoadingConfirmation(true);
      const response = await api.post('/exit-authorizations', confirmationData);
      if (response.status === 201) {
        response.data.employee = employees.find((emp) => emp.id === response.data.employee_id);
        setSubmittedData(response.data);
        setIsExitAuthCreated(true);
        setIsFormDisabled(true);
        formik.resetForm();
        //if (userRole.toString() !== "1") {
          navigate('/exit-authorizations');
        //}
      }
    } catch (error) {
      setErrorMessage("Erreur lors de la création de l'autorisation de sortie");
      console.error('Erreur:', error);
      
      // Handle duplicate error
      if (error.response?.status === 409) {
        setErrorMessage('Une autorisation de sortie identique a déjà été créée récemment. Veuillez patienter.');
      }
    } finally {
      setLoadingConfirmation(false);
      setConfirmationData(null);
    }
  };

  const handleCancel = () => {
    setConfirmationData(null);
  };

  const handleClose = () => {
    formik.resetForm();
    setConfirmationData(null);
    setIsExitAuthCreated(false);
    setErrorMessage(null);
    onClose();
  };

  // Fetch user role on component mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await fetchCurrentUser();
        if (currentUser) {
          setUserRole(currentUser.role); // Set the user's role
          setEmployee(currentUser.employee); // Set the user's employee
          // Reset form disabled state for all users accessing the form
          setIsFormDisabled(false);
          setIsExitAuthCreated(false);
          setSubmittedData(null);
          if (currentUser.role?.toString() !== "1" && currentUser.employee?.id) {
            formik.setFieldValue('employeeId', currentUser.employee.id);
          }
        } else {
          console.error('No current user found');
        }
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setLoading(false); // Stop loading in all cases
      }
    };
    loadUser();
  }, []); // Remove formik dependency to prevent infinite loop

  // Update form when employee data is loaded (for non-admin users)
  useEffect(() => {
    if (employee && userRole?.toString() !== "1" && userRole?.toString() !== "5" && userRole !== 'admin-tenes') {
      formik.setFieldValue('employeeId', employee.id);
    }
  }, [employee, userRole]); // Remove formik from dependencies

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Nouvelle autorisation de sortie</DialogTitle>
      <DialogContent>
        {errorMessage && <Typography color="error" sx={{ mb: 2 }}>{errorMessage}</Typography>}
        <Box sx={{ mt: 1 }}>
          {loadingConfirmation ? (
            <LinearProgress />
          ) : !confirmationData ? (
            <form onSubmit={formik.handleSubmit}>
              {/* Employee Selector */}
              <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                <InputLabel>Employé</InputLabel>
                <Select
                  name="employeeId"
                  value={userRole?.toString() === "1" || userRole?.toString() === "5" || userRole === 'admin-tenes' ? formik.values.employeeId : (employee?.id || '')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}
                  disabled={isFormDisabled}
                >
                  {(userRole?.toString() === "1" || userRole?.toString() === "5" || userRole === 'admin-tenes') ? (
                    employees.length > 0 ? (
                      employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {`${emp.first_name} ${emp.last_name}`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        Chargement des employés...
                      </MenuItem>
                    )
                  ) : (
                    <MenuItem value={employee?.id || ''}>
                      {employee ? `${employee.first_name} ${employee.last_name}` : 'Loading...'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Reason */}
              <TextField
                fullWidth
                margin="dense"
                multiline
                rows={3}
                name="reason"
                label="Motif"
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                disabled={isFormDisabled}
              />

              {/* Sortie Date and Time */}
              <TextField
                fullWidth
                margin="dense"
                type="date"
                name="sortie_date"
                label="Date de Sortie"
                InputLabelProps={{ shrink: true }}
                value={formik.values.sortie_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.sortie_date && Boolean(formik.errors.sortie_date)}
                helperText={formik.touched.sortie_date && formik.errors.sortie_date}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                disabled={isFormDisabled}
              />
              <TextField
                fullWidth
                margin="dense"
                type="time"
                name="sortie_time"
                label="Heure de Sortie"
                InputLabelProps={{ shrink: true }}
                value={formik.values.sortie_time}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.sortie_time && Boolean(formik.errors.sortie_time)}
                helperText={formik.touched.sortie_time && formik.errors.sortie_time}
                disabled={isFormDisabled}
              />

              {/* Entrée Date and Time */}
              <TextField
                fullWidth
                margin="dense"
                type="date"
                name="entree_date"
                label="Date d'Entrée"
                InputLabelProps={{ shrink: true }}
                value={formik.values.entree_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.entree_date && Boolean(formik.errors.entree_date)}
                helperText={formik.touched.entree_date && formik.errors.entree_date}
                inputProps={{ min: formik.values.sortie_date || new Date().toISOString().split('T')[0] }}
                disabled={isFormDisabled}
              />
              <TextField
                fullWidth
                margin="dense"
                type="time"
                name="entree_time"
                label="Heure d'Entrée"
                InputLabelProps={{ shrink: true }}
                value={formik.values.entree_time}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.entree_time && Boolean(formik.errors.entree_time)}
                helperText={formik.touched.entree_time && formik.errors.entree_time}
                disabled={isFormDisabled}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isFormDisabled}
                sx={{ mt: 2 }}
              >
                Créer
              </Button>
            </form>
          ) : (
            <Box>
              <Typography variant="h6">Confirmation de création</Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Employé :</Typography>
              <Typography>
                {employees.find(e => e.id === confirmationData.employeeId)?.first_name}{' '}
                {employees.find(e => e.id === confirmationData.employeeId)?.last_name}
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Motif :</Typography>
              <Typography>{confirmationData.reason}</Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Sortie :</Typography>
              <Typography>
                Le {confirmationData.sortie_date} à {confirmationData.sortie_time}
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Entrée :</Typography>
              <Typography>
                Le {confirmationData.entree_date} à {confirmationData.entree_time}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {!confirmationData ? (
          <Button onClick={handleClose} color="secondary">
            Annuler
          </Button>
        ) : (
          <>
            <Button onClick={handleCancel} color="secondary">
              Annuler
            </Button>
            <Button onClick={handleConfirm} color="primary" variant="contained">
              Confirmer
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExitAuthorizationForm;